import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Skeleton} from '@/components/ui/skeleton'
import {HelpIcon} from '@/components/help-icon'
import {DataTable, type Column} from '@/components/data-table'
import {ListCard} from '@/components/section-card'
import {DialogSubmitFooter} from '@/components/dialog-submit-footer'
import {AmountIvaFields, validateAmountIva} from '@/components/cartera/amount-iva-fields'
import {DescuentoAtajo} from '@/components/cartera/price-form-controls'
import {
  ALL_SEGMENT_KEYS,
  usePriceGrid,
  useSetPrice,
  type BillingCycle,
  type PriceBookEntry,
} from '@/hooks/inci/cartera/use-cartera'
import {formatCopFull} from '@/lib/money'
import {translateApiError} from '@/lib/errors'

const CYCLES: BillingCycle[] = ['mensual', 'anual']

type DialogTarget = {segmentKey: string; cycle: BillingCycle; existing: PriceBookEntry | undefined} | null

function PriceCell({entry, onClick, testId}: {entry: PriceBookEntry | undefined; onClick: () => void; testId: string}) {
  const {t} = useTranslation()
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-md border border-transparent px-2 py-1.5 text-left hover:border-border hover:bg-secondary/50">
      {entry ? (
        <span className="font-mono text-sm text-foreground tabular-nums">{formatCopFull(entry.amount_cop)}</span>
      ) : (
        <span className="text-sm text-muted-foreground">{t('cartera.precios.sinConfigurar')}</span>
      )}
    </button>
  )
}

function PriceDialog({target, onClose}: {target: DialogTarget; onClose: () => void}) {
  const {t} = useTranslation()
  const setPrice = useSetPrice()
  const [amount, setAmount] = useState('')
  const [iva, setIva] = useState('19')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!target) return
    setError(null)
    setAmount(target.existing ? String(target.existing.amount_cop) : '')
    setIva(target.existing ? String(target.existing.iva_pct) : '19')
  }, [target])

  function handleOpenChange(open: boolean) {
    if (!open) {
      setError(null)
      setPrice.reset()
      onClose()
    }
  }

  function blurValidate() {
    if (!amount.trim() || !iva.trim()) {
      setError(null)
      return
    }
    const validated = validateAmountIva(t, amount, iva)
    setError('error' in validated ? validated.error : null)
  }

  function submit() {
    if (!target) return
    const validated = validateAmountIva(t, amount, iva)
    if ('error' in validated) {
      setError(validated.error)
      return
    }
    setError(null)
    setPrice.mutate(
      {
        item_kind: 'saas',
        segment_key: target.segmentKey,
        billing_cycle: target.cycle,
        amount_cop: Math.round(validated.amountNum),
        iva_pct: Math.round(validated.ivaNum),
      },
      {onSuccess: () => handleOpenChange(false), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open={!!target} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="cartera-precio-dialog">
        <DialogHeader>
          <DialogTitle>
            {target &&
              t('cartera.precios.dialogTitle', {
                segment: target.segmentKey,
                cycle: t(`cartera.precios.${target.cycle}`),
              })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {target?.existing && (
            <DescuentoAtajo baseAmount={target.existing.amount_cop} onApply={(value) => setAmount(String(value))} />
          )}
          <AmountIvaFields
            idPrefix="cartera-precio"
            amount={amount}
            iva={iva}
            onAmountChange={setAmount}
            onIvaChange={setIva}
            onBlur={blurValidate}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogSubmitFooter
          onCancel={() => handleOpenChange(false)}
          onSubmit={submit}
          submitLabel={t('cartera.precios.submit')}
          pending={setPrice.isPending}
          testId="cartera-precio-submit"
        />
      </DialogContent>
    </Dialog>
  )
}

/** Siempre las 10 bandas × 2 ciclos: una celda sin precio dice «sin configurar». Guardar crea
 * una fila vigente nueva (supersede, nunca edita en sitio). */
export function PreciosTab() {
  const {t} = useTranslation()
  const {data, isLoading, isError, error} = usePriceGrid(true)
  const [target, setTarget] = useState<DialogTarget>(null)

  // El andamio lo pone el cliente y el servidor solo lo rellena: si la consulta falla, la tabla
  // no queda en cero filas.
  const porCelda = new Map<string, PriceBookEntry | null>()
  for (const celda of data?.celdas ?? []) {
    porCelda.set(`${celda.segment_key}|${celda.billing_cycle}`, celda.entry ?? null)
  }

  if (isLoading && !data) return <Skeleton className="h-96 w-full" />

  const columns: Column<string>[] = [
    {
      key: 'segment',
      header: t('cartera.precios.segment'),
      cell: (segmentKey) => <span className="font-mono text-sm text-foreground">{segmentKey}</span>,
    },
    ...CYCLES.map((cycle): Column<string> => ({
      key: cycle,
      header: t(`cartera.precios.${cycle}`),
      cell: (segmentKey) => {
        const existing = porCelda.get(`${segmentKey}|${cycle}`) ?? undefined
        return (
          <PriceCell
            entry={existing}
            testId={`cartera-precio-cell-${segmentKey}-${cycle}`}
            onClick={() => setTarget({segmentKey, cycle, existing})}
          />
        )
      },
    })),
  ]

  return (
    <ListCard
      testId="cartera-precios-base"
      title={t('cartera.tabs.precios')}
      actions={
        <HelpIcon label={t('cartera.precios.tipLabel')} testId="cartera-precios-tip">
          {t('cartera.precios.tip')}
        </HelpIcon>
      }>
      {isError && (
        <p className="notice-amber mb-3 rounded-lg px-3 py-2 text-sm" data-testid="cartera-precios-error">
          {t('cartera.precios.errorCarga')} {translateApiError(error)}
        </p>
      )}
      <DataTable columns={columns} rows={ALL_SEGMENT_KEYS} rowKey={(segmentKey) => segmentKey} />
      <PriceDialog target={target} onClose={() => setTarget(null)} />
    </ListCard>
  )
}
